import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { styled, useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import useAdd from '../../api/useAdd';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { formatShamsiDate, formatGregorianDate, formatHijriDate } from '../../utils/dateUtils';
import LocalizedDatePicker from '../common/LocalizedDatePicker';
import { useLazyUsers } from '../../hooks/useLazyUsers';
import { RTLTextField, RTLFormControl, RTLAutocomplete, RTLButton, RTLDialogTitle, RTLDialogContent, RTLDialogActions } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

const TaskAssignForm = ({ open, onClose, documentId, onSuccess }) => {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const { user } = useAuth();
  const token = user?.token;
  const { isRTL, getRTLProps } = useRTL();
  const theme = useTheme();
  const { dateFormat } = useDateFormat();
  
  // Assignment type (always user now - department toggle removed)
  const [assignmentType, setAssignmentType] = useState('user');
  
  // Form fields
  const [assigneeId, setAssigneeId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  
  // Hooks for API operations
  const { handleAdd: createAssignment, loading: assignmentLoading, success: assignmentSuccess } = useAdd('task-assignments', token);
  
  // Fetch document information if provided
  const { 
    data: documentData,
    isLoading: documentLoading
  } = useFetchObjects(
    documentId ? `documents/${documentId}` : null,
    {},
    token
  );
  
  // Fetch users and departments for dropdowns - moved after token is defined
  const { 
    users: usersData, 
    loading: usersLoading,
    error: usersError,
    fetchUsers
  } = useLazyUsers(token);
  
  
  // Helper function to get users from API response
  const getUsers = () => {
    return usersData || [];
  };
  
  // Get document data safely
  const getDocument = () => {
    if (!documentData) return null;
    if (documentData.data) return documentData.data;
    return documentData;
  };
  
  // Is form valid
  const isFormValid = () => {
    if (!assigneeId) return false;
    if (!documentId) return false;
    return true;
  };
  
  // Reset form when dialog is opened and load all users
  useEffect(() => {
    if (open) {
      setAssigneeId('');
      setDepartmentId('');
      setPriority('MEDIUM');
      setDueDate(null);
      setNotes('');
      setError(null);
      
      // Load all users when dialog opens
      fetchUsers();
    }
  }, [open, fetchUsers]);
  
  // Handle success
  useEffect(() => {
    if (assignmentSuccess) {
      onSuccess();
      onClose();
    }
  }, [assignmentSuccess, onClose, onSuccess]);
  
  // Set error if there's an API error
  useEffect(() => {
    if (usersError) {
      setError(`Error loading users: ${usersError.message}`);
    }
  }, [usersError]);
  
  // Load users when needed
  useEffect(() => {
    if (!usersData.length && !usersLoading && open) {
      fetchUsers();
    }
  }, [usersData.length, usersLoading, fetchUsers, open]);
  
  const handleSubmit = async () => {
    try {
      setError(null);
      
      // Validate form
      if (!assigneeId) {
        setError(t('tasks:error_select_user', 'Please select a user'));
        return;
      }
      
      if (!documentId) {
        setError(t('tasks:error_missing_document', 'Document information is missing'));
        return;
      }
      
      // Create assignment data
      const assignmentData = {
        document_id: documentId,
        assignee_id: assigneeId,
        notes,
        status: 'ASSIGNED',
        due_date: dueDate || undefined,
        assigned_at: new Date().toISOString(),
        assigner_id: user.id
      };
      
      // Create the assignment
      await createAssignment(assignmentData);
      
    } catch (err) {
      console.error('Error creating assignment:', err);
      setError(err.response?.data?.message || t('tasks:assignment_error', 'Failed to create assignment'));
    }
  };
  
  // Loading state
  const loading = assignmentLoading || documentLoading || usersLoading;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <RTLDialogTitle {...getRTLProps('dialogtitle')}>{t('tasks:assign_task', 'Assign Task')}</RTLDialogTitle>
      <RTLDialogContent {...getRTLProps('dialogcontent')}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, textAlign: isRTL ? 'right' : 'left' }}>
          {t('tasks:assignment_details', 'Assignment Details')}
        </Typography>
        
        {documentId && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
              {documentLoading ? (
                t('tasks:loading_document', 'Loading document information...')
              ) : (
                <>
                  {t('documents:document', 'Document')}: {getDocument()?.title || documentId}
                  {getDocument()?.reference_number && (
                    <> ({t('documents:reference', 'Ref')}: {getDocument().reference_number})</>
                  )}
                </>
              )}
            </Alert>
          </Box>
        )}
        
        {usersError && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
            {t('tasks:error_loading_users', 'Warning: Could not load all users. Showing limited selection.')}
          </Alert>
        )}
        
       
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                {usersLoading 
                  ? t('common:loading_users', 'Loading users...') 
                  : t('tasks:users_available', '{{count}} users available', { count: getUsers().length })
                }
              </Typography>
            </Box>
            <RTLAutocomplete
              fullWidth
              {...getRTLProps('autocomplete')}
              options={getUsers()}
              getOptionLabel={(option) => option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
              value={getUsers().find(user => user.id === assigneeId) || null}
              onChange={(event, newValue) => setAssigneeId(newValue?.id || '')}
              onOpen={() => {
                // Trigger lazy loading when autocomplete is opened
                if (getUsers().length === 0 && !usersLoading) {
                  fetchUsers();
                }
              }}
              loading={usersLoading}
              disabled={loading}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option => {
                  const fullName = (option.full_name || '').toLowerCase();
                  const username = (option.username || '').toLowerCase();
                  const email = (option.email || '').toLowerCase();
                  const department = (option.department?.name || '').toLowerCase();
                  
                  return fullName.includes(searchTerm) || 
                         username.includes(searchTerm) || 
                         email.includes(searchTerm) ||
                         department.includes(searchTerm);
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('tasks:assignee', 'Assignee')}
                  placeholder={t('tasks:search_user', 'Search by name, username, email, or department...')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              )}
              noOptionsText={t('tasks:no_users_found', 'No users found')}
              loadingText={t('common:loading_users', 'Loading users...')}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ p: 2, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f8f9fa' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        flexShrink: 0
                      }}
                    >
                      {(option.full_name || option.username || 'U').charAt(0).toUpperCase()}
                    </Box>
                    <Box sx={{ flexGrow: 1, textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
                      <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                        {option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {option.email}
                      </Typography>
                      {option.department?.name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {option.department.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              sx={{
                '& .MuiAutocomplete-paper': {
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'divider',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <LocalizedDatePicker
              label={`${t('common:due_date', 'Due Date')} (${t('common:optional', 'Optional')})`}
              value={dueDate}
              onChange={(e)=>setDueDate(e.target.value)}
              name="dueDate"
              isRtl={isRTL}
              inputProps={{ disabled: loading }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <RTLTextField
              label={t('tasks:notes', 'Notes')}
              multiline
              rows={3}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder={t('tasks:notes_placeholder', 'Add any relevant instructions or context for this task')}
              {...getRTLProps('textfield')}
              sx={{
                '& .MuiInputBase-input': {
                  paddingRight: isRTL ? '14px' : undefined,
                },
              }}
            />
          </Grid>
        </Grid>
      </RTLDialogContent>
      <RTLDialogActions {...getRTLProps('dialogactions')}>
        <RTLButton onClick={onClose} disabled={loading} {...getRTLProps('button')}>
          {t('common:cancel', 'Cancel')}
        </RTLButton>
        <RTLButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || !isFormValid()}
          startIcon={!isRTL && loading && <CircularProgress size={20} />}
          endIcon={isRTL && loading && <CircularProgress size={20} />}
          {...getRTLProps('button')}
        >
          {loading ? t('common:processing', 'Processing...') : t('tasks:assign', 'Assign')}
        </RTLButton>
      </RTLDialogActions>
    </Dialog>
  );
};

export default TaskAssignForm; 